import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import Iconify from 'src/components/iconify';

const CHROME_EXTENSION_URL =
  'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn';
const APP_STORE_URL = 'https://apps.apple.com/app/metamask/id1438144202';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=io.metamask';
const METAMASK_DOWNLOAD_URL = 'https://metamask.io/download/';

function getMobilePlatform() {
  if (typeof navigator === 'undefined') return 'unknown';

  const userAgent = navigator.userAgent || '';

  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios';
  if (/android/i.test(userAgent)) return 'android';

  return 'unknown';
}

function getDownloadLink(isMobile) {
  if (!isMobile) return CHROME_EXTENSION_URL;

  const platform = getMobilePlatform();

  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;

  return METAMASK_DOWNLOAD_URL;
}

function getInstallCtaLabel(isMobile) {
  if (!isMobile) return 'Add Chrome extension';

  const platform = getMobilePlatform();

  if (platform === 'ios') return 'Get iOS app';
  if (platform === 'android') return 'Get Android app';

  return 'Get MetaMask app';
}

const NOOP = () => {};

const OPEN_LINK_HINT =
  'Approve in MetaMask, then come back to this page. No request in MetaMask? Tap Continue to MetaMask again.';

const DEFAULT_INFO_TEXT =
  "Already have MetaMask? Tap Connect below. Don't have it yet? Install it first, then come back and press Connect.";
const IN_APP_INFO_TEXT =
  'On a phone, the most reliable way is to open SafiSwap inside the MetaMask app. You can also connect from this browser.';

function getInfoText(openInAppLink) {
  if (openInAppLink) return IN_APP_INFO_TEXT;
  return DEFAULT_INFO_TEXT;
}

function getPaperShadow(mode) {
  if (mode === 'light') return '0 24px 64px rgba(16, 24, 40, 0.18)';
  return '0 24px 64px rgba(0, 0, 0, 0.45)';
}

function getHintText(openLink, hint) {
  if (openLink) return OPEN_LINK_HINT;
  return hint;
}

const PRIMARY_BUTTON_SX = {
  height: 48,
  borderRadius: 2.5,
  textTransform: 'none',
  fontWeight: 800,
  fontSize: '0.9rem',
  letterSpacing: '-0.01em',
  boxShadow: 'none',
};

function getSecondaryButtonSx(theme) {
  return {
    height: 48,
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9rem',
    letterSpacing: '-0.01em',
    boxShadow: 'none',
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    bgcolor: alpha(theme.palette.background.default, 0.6),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.3),
      bgcolor: alpha(theme.palette.action.hover, 0.6),
      boxShadow: 'none',
    },
  };
}

// One dialog for the whole connect. On a phone browser:
//   Open in MetaMask app | Connect from this browser
//   → Preparing… → Continue to MetaMask (tap) → approve → back here, connected.
function renderPrimaryButton({ openLink, isConnecting, onConnect, openInAppLink, theme }) {
  const colorSx = {
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
  };

  // Phone browser, nothing started yet: open the site inside MetaMask's own
  // browser (always reliable), or connect from this browser through the relay.
  if (openInAppLink && !openLink && !isConnecting) {
    return (
      <>
        <Button
          fullWidth
          component="a"
          href={openInAppLink}
          rel="noopener"
          variant="contained"
          size="large"
          startIcon={<Icon icon="logos:metamask-icon" width={18} />}
          sx={{ ...PRIMARY_BUTTON_SX, ...colorSx }}
        >
          Open in MetaMask app
        </Button>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={onConnect}
          startIcon={<Iconify icon="solar:link-round-bold" width={18} />}
          sx={getSecondaryButtonSx(theme)}
        >
          Connect from this browser
        </Button>
      </>
    );
  }

  // The request is ready: the user's tap opens MetaMask with it.
  if (openLink) {
    return (
      <Button
        fullWidth
        component="a"
        href={openLink}
        rel="noopener"
        variant="contained"
        size="large"
        startIcon={<Icon icon="logos:metamask-icon" width={18} />}
        sx={{ ...PRIMARY_BUTTON_SX, ...colorSx }}
      >
        Continue to MetaMask
      </Button>
    );
  }

  // Phone browser: the connector is setting up the request (1–3 seconds).
  if (isConnecting && openInAppLink) {
    return (
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled
        startIcon={<CircularProgress size={16} color="inherit" />}
        sx={{ ...PRIMARY_BUTTON_SX, ...colorSx }}
      >
        Preparing…
      </Button>
    );
  }

  if (isConnecting) {
    return (
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled
        sx={{ ...PRIMARY_BUTTON_SX, ...colorSx }}
      >
        <CircularProgress size={20} color="inherit" />
      </Button>
    );
  }

  return (
    <Button
      fullWidth
      variant="contained"
      size="large"
      onClick={onConnect}
      startIcon={<Iconify icon="solar:link-round-bold" width={18} />}
      sx={{ ...PRIMARY_BUTTON_SX, ...colorSx }}
    >
      Connect
    </Button>
  );
}

MetaMaskRequiredDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConnect: PropTypes.func,
  isConnecting: PropTypes.bool,
  isMobile: PropTypes.bool,
  hint: PropTypes.string,
  openLink: PropTypes.string,
  openInAppLink: PropTypes.string,
};

// While connecting from a phone browser, keep a way out to the in-app
// browser in case the relay connection does not come through.
function renderInAppFallback(openInAppLink, isConnecting) {
  if (!openInAppLink || !isConnecting) return null;

  return (
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
      {'Taking too long? '}
      <Box
        component="a"
        href={openInAppLink}
        rel="noopener"
        sx={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}
      >
        Open SafiSwap in the MetaMask app
      </Box>
    </Typography>
  );
}

export default function MetaMaskRequiredDialog({
  open = false,
  onClose = NOOP,
  onConnect = NOOP,
  isConnecting = false,
  isMobile = false,
  hint = '',
  openLink = '',
  openInAppLink = '',
}) {
  const theme = useTheme();
  const hintText = getHintText(openLink, hint);

  const downloadLink = getDownloadLink(isMobile);
  const installCtaLabel = getInstallCtaLabel(isMobile);

  const handleGetMetaMask = () => {
    window.open(downloadLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      fullWidth
      maxWidth="xs"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.52),
            backdropFilter: 'blur(4px)',
          },
        },
        paper: {
          sx: {
            width: 'clamp(320px, 92vw, 420px)',
            boxSizing: 'border-box',
            maxWidth: 420,
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: getPaperShadow(theme.palette.mode),
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.07
            )} 0%, transparent 28%)`,
          },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.25 },
          pb: 1.5,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="logos:metamask-icon" width={22} />
            </Box>

            <Box sx={{ pr: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  mt: 0.25,
                }}
              >
                Connect with MetaMask
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75, lineHeight: 1.65 }}
              >
                MetaMask is a crypto wallet that lets you hold, send, and receive crypto, and
                connect securely to apps like this one.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={onClose}
            aria-label="Close MetaMask dialog"
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.default, 0.72),
              flexShrink: 0,
            }}
          >
            <Iconify icon="mingcute:close-line" width={18} />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {/* ── Body ── */}
      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: 2,
          pb: { xs: 2.5, sm: 3 },
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            px: 2,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ mt: 0.1, color: theme.palette.primary.main, flexShrink: 0 }}>
              <Iconify icon="solar:info-circle-bold" width={18} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {getInfoText(openInAppLink)}
            </Typography>
          </Stack>
        </Box>

        <Stack spacing={1.25}>
          {renderPrimaryButton({ openLink, isConnecting, onConnect, openInAppLink, theme })}

          {Boolean(hintText) && (
            <Typography variant="caption" color="warning.main" sx={{ lineHeight: 1.5 }}>
              {hintText}
            </Typography>
          )}

          {renderInAppFallback(openInAppLink, isConnecting)}

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleGetMetaMask}
            startIcon={<Iconify icon="solar:download-minimalistic-bold" width={18} />}
            sx={getSecondaryButtonSx(theme)}
          >
            {installCtaLabel}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
